import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
  page: number;
  totalPages: number;
  onChange: (page: number) => void;
}

function range(page: number, total: number): (number | '…')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, total, page - 1, page, page + 1]);
  if (page <= 3) [2, 3, 4].forEach((p) => pages.add(p));
  if (page >= total - 2) [total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - (sorted[i - 1] as number) > 1) out.push('…');
    out.push(p);
  });
  return out;
}

const Pagination: React.FC<PaginationProps> = ({ page, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  const go = (p: number) => {
    onChange(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const btn = 'h-10 min-w-[40px] px-3 rounded-full font-poppins text-[13px] font-medium inline-flex items-center justify-center transition-colors';

  return (
    <nav className="flex items-center justify-center gap-1.5 mt-12" aria-label="Pagination">
      <button type="button" onClick={() => go(page - 1)} disabled={page <= 1} className={`${btn} border border-black/15 hover:border-black disabled:opacity-30`} aria-label="Previous page">
        <ChevronLeft className="w-4 h-4" />
      </button>
      {range(page, totalPages).map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="px-1 text-black/40">…</span>
        ) : (
          <button
            key={p}
            type="button"
            onClick={() => go(p)}
            aria-current={p === page ? 'page' : undefined}
            className={`${btn} ${p === page ? 'text-white' : 'hover:bg-black/5'}`}
            style={p === page ? { backgroundColor: '#78BE20' } : undefined}
          >
            {p}
          </button>
        ),
      )}
      <button type="button" onClick={() => go(page + 1)} disabled={page >= totalPages} className={`${btn} border border-black/15 hover:border-black disabled:opacity-30`} aria-label="Next page">
        <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
};

export default Pagination;
